import {
  WSActions,
  WSElement,
  WSSectionToolbar,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { PayeeRow, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import { useQueryWorkLogsWithPayees } from "../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import {
  routeHelpersWorkLogPayerDetails,
  routeHelpersWorkLogsPayer
} from "../paths";
import { WorkLogPayerStatusCell } from "./WorkLogPayerStatusCell";
import { createListRequest } from "./TableWorkLogsPayerView/filters/request";
import { Filter } from "./TableWorkLogsPayerView/filters/types";

type Props = {
  filter?: Filter;
  title?: string;
  showEngagementDetails?: boolean;
  hideViewAll?: boolean;
};

export const WidgetWorkLogsPayerView: React.FC<Props> = ({
  filter,
  title = "Work logs",
  showEngagementDetails = false,
  hideViewAll = false
}) => {
  const { accountId } = useSession();
  const history = useHistory();

  // Build API request with 5-item page size
  const request = useMemo(
    () => ({
      ...createListRequest(accountId, filter),
      page: { size: 5 }
    }),
    [accountId, filter]
  );

  const queryWorkLogs = useQueryWorkLogsWithPayees(request, {
    joinPayee: showEngagementDetails
  });
  const workLogs = useMemo(
    () => queryWorkLogs.data || [],
    [queryWorkLogs.data]
  );

  // Build payeesMap from work logs with joined payee data
  const payeesMap = useMemo(() => {
    const map = new Map<string, PayeeRow>();
    if (showEngagementDetails) {
      (workLogs || []).forEach(workLog => {
        // TODO(v3): payee join keyed by `payeeEngagementId` (v1 `payeeId` dropped);
        // PayeeRow.id ≈ payeeEngagementId, id-space alignment unverified.
        if (workLog.payee && workLog.payeeEngagementId) {
          map.set(workLog.payeeEngagementId, workLog.payee);
        }
      });
    }
    return map;
  }, [showEngagementDetails, workLogs]);

  // Build engagement names map: workLogId -> engagementName
  const engagementNamesMap = useMemo(() => {
    if (!showEngagementDetails) return new Map<string, string>();

    const map = new Map<string, string>();
    workLogs.forEach(workLog => {
      const payee = payeesMap.get(workLog.payeeEngagementId ?? "");
      const engagement = payee?.engagements?.find(
        e => e.payeeEngagementId === workLog.payeeEngagementId
      );
      if (engagement?.engagementName) {
        map.set(workLog.id!, engagement.engagementName);
      }
    });
    return map;
  }, [showEngagementDetails, workLogs, payeesMap]);

  // Build external IDs map: workLogId -> externalId
  const externalIdsMap = useMemo(() => {
    if (!showEngagementDetails) return new Map<string, string>();

    const map = new Map<string, string>();
    workLogs.forEach(workLog => {
      const payee = payeesMap.get(workLog.payeeEngagementId ?? "");
      const externalId = payee?.externalId;
      if (externalId) {
        map.set(workLog.id!, externalId);
      }
    });
    return map;
  }, [showEngagementDetails, workLogs, payeesMap]);

  // Helper to build secondary text from the two maps
  const getSecondaryText = (workLogId: string): string | undefined => {
    const engagementName = engagementNamesMap.get(workLogId);
    const externalId = externalIdsMap.get(workLogId);

    if (engagementName && externalId) {
      return `${engagementName} • ${externalId}`;
    }
    return engagementName || externalId;
  };

  if (!queryWorkLogs.data?.length) {
    return null;
  }

  return (
    <WSElement>
      <WSSectionToolbar title={title} mb="S" />

      <WSTable<WorkLog>
        tableData={workLogs.map(workLog => ({
          id: workLog.id!,
          data: workLog
        }))}
        onRowClick={row => {
          history.push(
            routeHelpersWorkLogPayerDetails.createLocationObject({
              workLogId: row.id
            })
          );
        }}
        columns={[
          {
            config: {
              header: { text: "Work logs" },
              gridTemplateSizeMax: "2fr"
            },
            renderFunction: ({ data }) => (
              <WSTableCell
                text={`#${data.workLogNumber}`}
                secondaryText={
                  showEngagementDetails ? getSecondaryText(data.id!) : undefined
                }
              />
            )
          },
          {
            config: {
              header: { text: "Status" },
              justify: "end"
            },
            renderFunction: ({ data }) => (
              <WorkLogPayerStatusCell workLog={data} />
            )
          }
        ]}
      />

      {!hideViewAll && (
        <WSActions
          alignment="left"
          size="M"
          mt="M"
          buttons={[
            {
              label: "View all",
              kind: "Link",
              onClick: () => {
                history.push(
                  routeHelpersWorkLogsPayer.createLocationObject({
                    search: { filter: filter || {} }
                  })
                );
              }
            }
          ]}
        />
      )}
    </WSElement>
  );
};
